import React from "react";

import { formatSkillFrontmatterEntryLabel } from "../../utils/skill-frontmatter";

import { useFrontmatterEditState } from "./hooks/useFrontmatterEditState";
import { FrontmatterPanelBody } from "./FrontmatterPanelBody";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

interface SkillCollectionFrontmatterPanelProps {
  frontmatter: string | null;
  editable?: boolean;
  onChange?: (frontmatter: string | null) => void;
  variant?: "full" | "summary";
}

export const SkillCollectionFrontmatterPanel = ({
  frontmatter,
  editable = false,
  onChange,
  variant = "full",
}: SkillCollectionFrontmatterPanelProps) => {
  const {
    isEditingMetadata,
    setIsEditingMetadata,
    accordionValue,
    setAccordionValue,
    entries,
    draftState,
    setDraftState,
    grouped,
    applyDraftState,
    cancelEditing,
  } = useFrontmatterEditState({ frontmatter, onChange });

  if (!frontmatter?.trim()) {
    return null;
  }

  if (variant === "summary") {
    const preferredKeys = [
      "name",
      "description",
      "metadata.author",
      "author",
      "metadata.version",
      "version",
    ];

    const preferred = preferredKeys
      .map(key => entries.find(entry => entry.key === key))
      .filter((entry): entry is { key: string; value: string } =>
        Boolean(entry && entry.value.trim().length > 0),
      );

    const summaryEntries =
      preferred.length > 0
        ? preferred.slice(0, 3)
        : entries.filter(entry => entry.value.trim().length > 0).slice(0, 3);

    if (summaryEntries.length === 0) {
      return null;
    }

    return (
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white/95 px-3 py-2 backdrop-blur supports-[backdrop-filter]:bg-white/85">
        <div className="truncate text-sm text-gray-700">
          {summaryEntries
            .map(entry => `${formatSkillFrontmatterEntryLabel(entry.key)}: ${entry.value}`)
            .join(" · ")}
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-gray-200 bg-gray-50/60 px-2 py-1.5">
      <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue}>
        <AccordionItem value="frontmatter" className="overflow-hidden border-0 bg-transparent">
          <div className="flex items-center justify-between gap-2 px-3">
            <AccordionTrigger
              chevronClassName="ml-2"
              className="px-0 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 hover:no-underline"
            >
              Frontmatter
            </AccordionTrigger>
            {editable && !isEditingMetadata && (
              <Button
                type="button"
                size="xs"
                variant="outline"
                className="h-7 px-2 text-xs"
                onClick={() => {
                  setAccordionValue("frontmatter");
                  setIsEditingMetadata(true);
                }}
              >
                Edit metadata
              </Button>
            )}
          </div>
          <AccordionContent className="p-0">
            <FrontmatterPanelBody
              isEditing={editable && !!onChange && isEditingMetadata}
              draftState={draftState}
              onDraftChange={setDraftState}
              onApply={applyDraftState}
              onCancel={cancelEditing}
              topLevelEntries={grouped.topLevelEntries}
              groups={grouped.groups}
              hasEntries={entries.length > 0}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
